/**
 * TODO API Service
 * Handles all TODO-related API calls to the backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Fetch all TODOs for a family
 */
export const fetchTodos = async (familyId, filters = {}) => {
    const { category, completed, skip = 0, limit = 100 } = filters;
    
    const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString()
    });
    
    if (category) params.append('category', category);
    if (completed !== undefined) params.append('completed', completed.toString());
    
    const response = await fetch(
        `${API_BASE_URL}/v1/families/${familyId}/todos?${params}`,
        {
            credentials: 'include'
        }
    );
    
    if (!response.ok) {
        throw new Error(`Failed to fetch todos: ${response.statusText}`);
    }
    
    return await response.json();
};

/**
 * Create a new TODO
 */
export const createTodo = async (familyId, todoData) => {
    const response = await fetch(
        `${API_BASE_URL}/v1/families/${familyId}/todos`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(todoData)
        }
    );
    
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || `Failed to create todo: ${response.statusText}`);
    }
    
    return await response.json();
};

/**
 * Update a TODO
 */
export const updateTodo = async (familyId, todoId, updates) => {
    const response = await fetch(
        `${API_BASE_URL}/v1/families/${familyId}/todos/${todoId}`,
        {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(updates)
        }
    );
    
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || `Failed to update todo: ${response.statusText}`);
    }
    
    return await response.json();
};

/**
 * Delete a TODO (soft delete)
 */
export const deleteTodo = async (familyId, todoId) => {
    const response = await fetch(
        `${API_BASE_URL}/v1/families/${familyId}/todos/${todoId}`,
        {
            method: 'DELETE',
            credentials: 'include'
        }
    );
    
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || `Failed to delete todo: ${response.statusText}`);
    }
};

/**
 * Toggle TODO completion status
 */
export const toggleTodoComplete = async (familyId, todoId) => {
    const response = await fetch(
        `${API_BASE_URL}/v1/families/${familyId}/todos/${todoId}/complete`,
        {
            method: 'PATCH',
            credentials: 'include'
        }
    );
    
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || `Failed to toggle todo: ${response.statusText}`);
    }
    
    return await response.json();
};

/**
 * Upload files to a TODO
 */
export const uploadTodoFiles = async (familyId, todoId, files) => {
    const formData = new FormData();
    files.forEach(file => {
        formData.append('files', file);
    });
    
    const response = await fetch(
        `${API_BASE_URL}/v1/families/${familyId}/todos/${todoId}/upload`,
        {
            method: 'POST',
            credentials: 'include',
            body: formData
        }
    );
    
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || `Failed to upload files: ${response.statusText}`);
    }
    
    return await response.json();
};

/**
 * Get file download URL
 */
export const getTodoFileUrl = (familyId, todoId, filename) => {
    return `${API_BASE_URL}/v1/files/${familyId}/todos/${todoId}/${filename}`;
};

